import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { Kbd } from "@/components/ui/primitives"
import { TooltipBorderedArrow } from "@/components/ui/tooltip-bordered-arrow"
import { cn } from "@/lib/utils"
import { useUiStore } from "@/stores/ui-store"

type CollapsedNavPeekContextValue = {
  railHovered: boolean
}

const CollapsedNavPeekContext = createContext<CollapsedNavPeekContextValue | null>(null)

const PEEK_SHOW_DELAY_MS = 40
const SIDEBAR_TOOLTIP_DELAY_MS = 50

export function CollapsedNavPeekProvider({
  children,
  enabled,
  railHovered,
}: {
  children: ReactNode
  enabled: boolean
  railHovered: boolean
}) {
  const [delayedHovered, setDelayedHovered] = useState(false)

  useEffect(() => {
    if (!enabled || !railHovered) {
      setDelayedHovered(false)
      return
    }
    const id = window.setTimeout(() => setDelayedHovered(true), PEEK_SHOW_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [enabled, railHovered])

  return (
    <CollapsedNavPeekContext.Provider value={enabled ? { railHovered: delayedHovered } : null}>
      {children}
    </CollapsedNavPeekContext.Provider>
  )
}

export function SidebarTooltip({
  children,
  label,
  details,
  shortcut,
  side = "right",
  elevated = false,
  tone = "default",
  open: openProp,
}: {
  children: ReactNode
  label: string
  details?: string[]
  shortcut?: string
  side?: "top" | "right" | "bottom"
  /** Above shot-panel dialog (z-101), below confirm (z-210). */
  elevated?: boolean
  tone?: "default" | "accent"
  /** Controlled open (e.g. auto-hint flash). When set, overrides peek/hover. */
  open?: boolean
}) {
  const peek = useContext(CollapsedNavPeekContext)
  // Rail stays visible next to the shot panel (z-101): its labels must open above it.
  const lifted = useUiStore((state) => elevated || state.shotPanelOpen)
  const [itemHovered, setItemHovered] = useState(false)
  const isPeek = peek != null
  const emphasized = !isPeek || itemHovered
  const accent = tone === "accent"
  const open = openProp !== undefined ? openProp : isPeek ? peek.railHovered : undefined

  return (
    <TooltipPrimitive.Root
      open={open}
      delayDuration={isPeek || openProp != null ? 0 : SIDEBAR_TOOLTIP_DELAY_MS}
      disableHoverableContent
    >
      <TooltipPrimitive.Trigger asChild>
        <span
          className="inline-flex"
          onMouseEnter={() => setItemHovered(true)}
          onMouseLeave={() => setItemHovered(false)}
        >
          {children}
        </span>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={8}
          className={cn(
            "group pointer-events-none rounded-lg font-medium shadow-[var(--ui-shadow-md)]",
            "animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1",
            accent
              ? "border border-ui-accent bg-ui-accent text-ui-on-accent"
              : "border border-ui-border-opaque bg-ui-bg-elevated",
            emphasized
              ? cn(lifted ? "z-[121]" : "z-[101]", "px-2.5 py-1.5 text-xs", !accent && "text-ui-text-primary")
              : cn(lifted ? "z-[120]" : "z-[100]", "px-2 py-1 text-[10px]", !accent && "text-ui-text-muted"),
          )}
        >
          {label}
          {shortcut ? (
            <Kbd className="ml-2 h-4 items-center px-1.5 text-[10px] leading-none sm:inline-flex">{shortcut}</Kbd>
          ) : null}
          {emphasized
            ? details?.map((line) => (
                <span key={line} className="block font-normal text-ui-text-muted">
                  {line}
                </span>
              ))
            : null}
          <TooltipBorderedArrow
            fillClassName={accent ? "fill-ui-accent" : "fill-ui-bg-elevated"}
            borderClassName={accent ? "stroke-ui-accent" : "stroke-ui-border-opaque"}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
